import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import type { RootState } from '../store/store';
import type { DpeAd } from '../types/ads';
import { JWTHeader } from '../utils/JWTHeader';

interface DpeDetails {
  remoteId?: string | number;
  identifiantDpe?: string;
  id?: string | number;
}

interface UseAdsModalReturn {
  /** Whether the ads modal is currently open */
  isAdsModalOpen: boolean;
  /** Array of ads fetched from the API */
  ads: DpeAd[];
  /** Loading state while fetching ads */
  adsLoading: boolean;
  /** Error message if fetch failed */
  adsError: string | null;
  /** Handler to fetch ads and open the modal - accepts DPE details object */
  handleAdsClick: (dpeDetails: DpeDetails) => Promise<void>;
  /** Handler to fetch ads and open the modal - for components that already have dpeId */
  handleAdsClickById: (dpeId: string | number) => Promise<void>;
  /** Close the ads modal */
  closeAdsModal: () => void;
  /** Props to spread directly onto AdsModal component */
  adsModalProps: {
    isOpen: boolean;
    onClose: () => void;
    ads: DpeAd[];
    isLoading: boolean;
    error: string | null;
  };
}

/**
 * Custom hook to manage the ads modal state and fetching logic.
 * 
 * @example
 * ```tsx
 * const { handleAdsClick, adsModalProps } = useAdsModal();
 * 
 * return (
 *   <>
 *     <button onClick={() => handleAdsClick(dpeDetails)}>View Ads</button>
 *     <AdsModal {...adsModalProps} />
 *   </>
 * );
 * ```
 */
export function useAdsModal(): UseAdsModalReturn {
  const jwt = useSelector((state: RootState) => state.auth.token);

  const [isAdsModalOpen, setIsAdsModalOpen] = useState(false);
  const [ads, setAds] = useState<DpeAd[]>([]);
  const [adsLoading, setAdsLoading] = useState(false);
  const [adsError, setAdsError] = useState<string | null>(null);

  const fetchAds = useCallback(async(dpeId: string | number) => {
    setIsAdsModalOpen(true);
    setAdsLoading(true);
    setAdsError(null);

    try {
      const response = await fetch(`/api/annonces/dpes/${dpeId}`, JWTHeader(jwt));

      if (response.status === 404) {
        // 404 means no ads found - this is not an error, just set empty array
        setAds([]);
        return;
      }

      if (!response.ok) {
        throw new Error('Erreur lors de la récupération des annonces');
      }

      const data = await response.json();
      setAds(data);
    } catch (error) {
      setAdsError(error instanceof Error ? error.message : 'Une erreur est survenue');
    } finally {
      setAdsLoading(false);
    }
  }, [jwt]);

  const handleAdsClickById = useCallback(async(dpeId: string | number) => {
    if (!dpeId) {
      toast.error('Aucun ID DPE trouvé');
      return;
    }
    await fetchAds(dpeId);
  }, [fetchAds]);

  const handleAdsClick = useCallback(async(dpeDetails: DpeDetails) => {
    const dpeId = dpeDetails?.remoteId || dpeDetails?.identifiantDpe || dpeDetails?.id;
    if (!dpeId) {
      toast.error('Aucun ID DPE trouvé');
      return;
    }
    await fetchAds(dpeId);
  }, [fetchAds]);

  const closeAdsModal = useCallback(() => {
    setIsAdsModalOpen(false);
  }, []);

  return {
    isAdsModalOpen,
    ads,
    adsLoading,
    adsError,
    handleAdsClick,
    handleAdsClickById,
    closeAdsModal,
    adsModalProps: {
      isOpen: isAdsModalOpen,
      onClose: closeAdsModal,
      ads,
      isLoading: adsLoading,
      error: adsError,
    },
  };
}

export default useAdsModal;
